import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useConfig } from '@/features/config/ConfigProvider'
import { ScannerView } from '@/features/scanner/ScannerView'
import { ScanForm, type FormValues } from '@/features/scan/ScanForm'
import { ScanResult, type SubmissionOutcome } from '@/features/scan/ScanResult'
import { errorFeedback } from '@/lib/feedback'
import { report } from '@/lib/telemetry'
import type { ScanHit } from '@/features/scanner/useDecoder'
import type { WorkflowPublic } from '@/lib/api-types'

interface Pending {
  hit: ScanHit
  workflow: WorkflowPublic
  /** Generated once per captured code, so every retry is the same submission. */
  idempotencyKey: string
  scannedAt: number
}

export function ScanScreen() {
  const navigate = useNavigate()
  const { activeWorkflow } = useConfig()

  const [pending, setPending] = useState<Pending | null>(null)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<SubmissionOutcome | null>(null)
  const [retrying, setRetrying] = useState(false)

  // Held in a ref so the retry handler always sees the current submission even
  // if the component re-renders between attempts.
  const lastRef = useRef<Pending | null>(null)

  const send = useCallback(async (job: Pending, values: FormValues) => {
    setBusy(true)
    lastRef.current = job

    try {
      const result = await api.submitScan({
        idempotencyKey: job.idempotencyKey,
        workflowId: job.workflow.id,
        code: job.hit.text,
        ...(job.hit.format !== null ? { codeFormat: job.hit.format } : {}),
        formData: values,
        scannedAt: job.scannedAt,
      })

      setOutcome({
        delivered: result.delivered,
        scan: result.scan,
        reason: result.delivered ? null : (result.scan.error ?? 'The webhook rejected this scan.'),
        stored: true,
        offline: false,
      })
      if (!result.delivered) errorFeedback()
    } catch (err) {
      errorFeedback()

      const apiError = err instanceof ApiError ? err : null
      // 503 not_configured still stores the scan and returns it in the error
      // payload, so a retry has something to act on.
      const attached = (apiError?.payload as { scan?: SubmissionOutcome['scan'] } | null)?.scan

      setOutcome({
        delivered: false,
        scan: attached ?? null,
        reason: apiError?.message ?? 'Could not reach the server.',
        stored: attached != null,
        offline: apiError?.code === 'offline',
      })

      report({
        level: 'error',
        event: 'scan.submit_failed',
        message: apiError?.message ?? String(err),
        context: { code: apiError?.code, workflowId: job.workflow.id },
      })
    } finally {
      setBusy(false)
      setPending(null)
    }
  }, [])

  const handleDetected = useCallback(
    (hit: ScanHit) => {
      if (activeWorkflow === null) return

      const job: Pending = {
        hit,
        workflow: activeWorkflow,
        // crypto.randomUUID needs a secure context; the app is HTTPS-only in
        // production, but a fallback keeps local http testing working.
        idempotencyKey:
          typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        scannedAt: Date.now(),
      }

      if (activeWorkflow.immediateSubmit) {
        void send(job, {})
      } else {
        setPending(job)
      }
    },
    [activeWorkflow, send],
  )

  const handleRetry = useCallback(async () => {
    const scanId = outcome?.scan?.id
    if (scanId === undefined) return

    setRetrying(true)
    try {
      // Retries the stored scan by id — it never creates a second record, no
      // matter how many times this is pressed.
      const result = await api.retryScan(scanId)
      setOutcome({
        delivered: result.delivered,
        scan: result.scan,
        reason: result.delivered ? null : (result.scan.error ?? 'Still not accepted.'),
        stored: true,
        offline: false,
      })
      if (!result.delivered) errorFeedback()
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null
      setOutcome((current) =>
        current === null
          ? current
          : {
              ...current,
              reason: apiError?.message ?? 'Retry failed.',
              offline: apiError?.code === 'offline',
            },
      )
      errorFeedback()
    } finally {
      setRetrying(false)
    }
  }, [outcome])

  if (activeWorkflow === null) {
    return (
      <div className="inset-safe flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-[0.9375rem] text-[var(--text-secondary)]">
          No workflow is selected yet.
        </p>
        <button
          type="button"
          onClick={() => void navigate('/')}
          className="min-h-touch rounded-2xl bg-brand-600 px-6 font-semibold text-white"
        >
          Choose a workflow
        </button>
      </div>
    )
  }

  return (
    <>
      <ScannerView
        formats={activeWorkflow.formats}
        workflowName={activeWorkflow.name}
        onDetected={handleDetected}
        onClose={() => void navigate('/')}
        // Freeze decoding while a form or result is up, so a code still in
        // frame cannot fire a second submission behind the dialog.
        paused={pending !== null || outcome !== null || busy}
      />

      {pending !== null && (
        <ScanForm
          workflow={pending.workflow}
          code={pending.hit.text}
          busy={busy}
          onSubmit={(values) => void send(pending, values)}
          onCancel={() => setPending(null)}
        />
      )}

      {outcome !== null && (
        <ScanResult
          outcome={outcome}
          retrying={retrying}
          onRetry={() => void handleRetry()}
          onDismiss={() => {
            setOutcome(null)
            void navigate('/')
          }}
          onScanAgain={() => setOutcome(null)}
        />
      )}
    </>
  )
}
